import {
  api,
  parseActivityProjection,
  parseCategory,
  parseList,
  parseSavedActivity,
} from '@/lib/api'
import type { QueryValue } from '@/lib/api/types'
import type { paths } from '@/lib/api/generated/schema'

import {
  type Activity,
  type Category,
  type DiscoveryLocation,
  type Friend,
  type MapBounds,
  type SavedActivity,
  type Schemas,
} from './types'
import { buildDateRange } from './filters/query'
import type { DiscoveryFilters } from './filters/filters'

type ListActivitiesQuery = NonNullable<
  paths['/api/v1/activities']['get']['parameters']['query']
>

const listQuery = (
  filters: DiscoveryFilters,
): Record<string, QueryValue> => {
  const query: Record<string, QueryValue> = {}
  if (filters.categorySlug !== 'all') query.category = filters.categorySlug
  if (filters.showPast) query.showPast = true
  if (filters.participationMode !== 'all') {
    query.participationMode = filters.participationMode
  }
  // minSpots: the Flutter app always sends the current value, including 1.
  query.minSpots = filters.minSpots
  if (filters.friendId) query.friendId = filters.friendId
  if (filters.location && filters.maxDistanceKm > 0) {
    // f472f9a: the selected city is a coordinate + radius search. Never send
    // the `city` query parameter, which the server matches exactly by name.
    query.latitude = filters.location.latitude
    query.longitude = filters.location.longitude
    query.radiusKm = filters.maxDistanceKm
  }
  const dateRange = buildDateRange(filters.dateRange)
  if (dateRange.from) query.from = dateRange.from
  if (dateRange.to) query.to = dateRange.to
  return query
}

function withSearchAndBounds(
  query: Record<string, QueryValue>,
  search: string,
  bounds: MapBounds | null,
): ListActivitiesQuery {
  const trimmed = search.trim()
  if (trimmed) query.q = trimmed
  if (bounds) {
    query.south = bounds.south
    query.north = bounds.north
    query.west = bounds.west
    query.east = bounds.east
  }
  return query as ListActivitiesQuery
}

export interface FetchActivitiesArgs {
  filters: DiscoveryFilters
  search: string
  bounds: MapBounds | null
}

/**
 * Reads discovery data. `GET /activities` has no saved parameter, so the
 * saved-only view intersects `/me/saved-activities` with the same server-side
 * query used by the list. That keeps category/date/radius/friend/text/bounds
 * semantics identical without duplicating filtering rules on the client.
 */
export async function fetchActivities(
  { filters, search, bounds }: FetchActivitiesArgs,
  signal?: AbortSignal,
): Promise<Activity[]> {
  if (filters.savedOnly) {
    const query = withSearchAndBounds(listQuery(filters), search, bounds)
    const [saved, filtered] = await Promise.all([
      discoveryApi.listSavedActivities(signal),
      discoveryApi.listActivities(query, signal),
    ])
    const byId = new Map(filtered.items.map((item) => [item.id, item]))
    return saved.items
      .map((item: SavedActivity) => byId.get(item.id))
      .filter((activity): activity is Activity => Boolean(activity))
      .map((activity) => ({ ...activity, saved: true }))
  }

  const query = withSearchAndBounds(listQuery(filters), search, bounds)
  const response = await discoveryApi.listActivities(query, signal)
  return response.items
}

export const discoveryApi = {
  listActivities(
    query: Record<string, QueryValue> = {},
    signal?: AbortSignal,
  ): Promise<Schemas['ActivityListResponse']> {
    return api
      .get<Schemas['ActivityListResponse']>('/activities', {
        query,
        signal,
      })
      .then((response) => ({
        ...response,
        items: parseList(
          response.items,
          '/activities',
          parseActivityProjection,
        ),
      }))
  },

  listAgenda(signal?: AbortSignal): Promise<Schemas['ActivityListResponse']> {
    return api
      .get<Schemas['ActivityListResponse']>('/activities', {
        query: { mine: 'true' },
        signal,
      })
      .then((response) => ({
        ...response,
        items: parseList(
          response.items,
          '/activities?mine=true',
          parseActivityProjection,
        ),
      }))
  },

  listHistory(signal?: AbortSignal): Promise<Schemas['ActivityHistoryResponse']> {
    return api
      .get<Schemas['ActivityHistoryResponse']>('/activities/history', {
        signal,
      })
      .then((response) => ({
        ...response,
        items: parseList(
          response.items,
          '/activities/history',
          parseActivityProjection,
        ),
      }))
  },

  listSavedActivities(
    signal?: AbortSignal,
  ): Promise<Schemas['SavedActivitiesResponse']> {
    return api
      .get<Schemas['SavedActivitiesResponse']>('/me/saved-activities', {
        signal,
      })
      .then((response) => ({
        ...response,
        items: parseList(
          response.items,
          '/me/saved-activities',
          parseSavedActivity,
        ),
      }))
  },

  listCategories(signal?: AbortSignal): Promise<Category[]> {
    return api
      .get<Category[]>('/categories', {
        signal,
        skipAuth: true,
      })
      .then((items) => parseList(items, '/categories', parseCategory))
  },

  listFriends(signal?: AbortSignal): Promise<Schemas['FriendsResponse']> {
    return api.get<Schemas['FriendsResponse']>('/friends', { signal })
  },

  saveActivity(activityId: string): Promise<{ activityId: string; saved: true }> {
    return api.post<{ activityId: string; saved: true }>(
      `/activities/${activityId}/save`,
    )
  },

  unsaveActivity(
    activityId: string,
  ): Promise<{ activityId: string; saved: false }> {
    return api.delete<{ activityId: string; saved: false }>(
      `/activities/${activityId}/save`,
    )
  },

  unreadNotificationCount(signal?: AbortSignal): Promise<{ count: number }> {
    return api.get<{ count: number }>('/notifications/unread-count', { signal })
  },

  suggestCities(
    query: string,
    signal?: AbortSignal,
  ): Promise<Schemas['CitySuggestionsResponse']> {
    return api.post<Schemas['CitySuggestionsResponse']>(
      '/location/suggestions',
      { query },
      { signal, skipAuth: true, skipRefresh: true },
    )
  },

  reverseGeocode(
    latitude: number,
    longitude: number,
    signal?: AbortSignal,
  ): Promise<Schemas['ReverseGeocodeResponse']> {
    return api.post<Schemas['ReverseGeocodeResponse']>(
      '/location/reverse-geocode',
      { latitude, longitude },
      { signal, skipAuth: true, skipRefresh: true },
    )
  },
}

export type { SavedActivity, Friend, DiscoveryLocation }
